import { Variant } from '@ausbom/button/lib/Button';
import { clsx } from 'clsx';
import React, { MouseEventHandler, ReactNode } from 'react';
import { ExternalLink12 } from '../Icons/Icons';
import './Link.scss';

type LinkProps = {
  dark?: boolean;
  href?: string;
  title?: string;
  blank?: boolean;
  target?: string;
  rel?: string;
  external?: boolean; // Will force noopener noreferrer nofollow and new window
  externalLinkIcon?: boolean; // Show external link icon
  role?: string;
  aria?: {
    label?: string;
  };
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  style?: 'footer' | 'body' | Variant;
  icon?: ReactNode;
  className?: string;
  children?: React.ReactNode;
  'data-testid'?: string;
};

const NEW_TAB_SUFFIX = 'Opens in a new tab';

function withTrailingFullStop(value: string): string {
  return /[.!?]$/.test(value) ? value : `${value}.`;
}

function getAriaLabel(
  children: ReactNode, // Link content used to detect icon-only links
  childText: string, // Extracted visible text content from children
  isExternalLinkWithIcon: boolean, // Append new-tab suffix when the external link icon is shown
  title?: string,
  ariaLabel?: string
): string | undefined {
  if (ariaLabel) {
    return isExternalLinkWithIcon ? `${withTrailingFullStop(ariaLabel)} ${NEW_TAB_SUFFIX}` : ariaLabel;
  }

  if (childText) {
    return isExternalLinkWithIcon ? `${withTrailingFullStop(childText)} ${NEW_TAB_SUFFIX}` : undefined;
  }

  if (title) {
    if (isExternalLinkWithIcon) {
      return `${withTrailingFullStop(title)} ${NEW_TAB_SUFFIX}`;
    }

    return children == null ? undefined : title;
  }

  return undefined;
}

function getTextContent(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getTextContent).join('');
  }

  if (React.isValidElement(node)) {
    return getTextContent(node.props.children);
  }

  return '';
}

export default function Link(props: LinkProps) {
  const {
    dark = false,
    href,
    title,
    blank = false,
    target,
    rel,
    external = false,
    externalLinkIcon = false,
    role = 'link',
    aria,
    onClick,
    style = 'body',
    icon,
    className,
    children,
    'data-testid': dataTestId
  } = props;

  const showIcon = externalLinkIcon && external;
  const resolvedTarget = external || blank ? '_blank' : (target ?? '_self');
  const childText = getTextContent(children).trim();
  const ariaLabel = getAriaLabel(children, childText, showIcon && resolvedTarget === '_blank', title, aria?.label);

  return (
    <a
      href={href}
      title={title}
      target={resolvedTarget}
      rel={external ? 'noopener noreferrer nofollow' : rel}
      role={role ?? 'link'}
      onClick={onClick}
      aria-label={ariaLabel}
      className={clsx(style, className, { external: external, 'elf-external': showIcon, dark: dark })}
      data-testid={dataTestId}
    >
      {icon && <span className={`${style}__icon`}>{icon}</span>}
      {children ?? (style === 'primary' ? <span className={`${style}__label`}>{title}</span> : <>{title}</>)}
      {showIcon && (
        <span className="elf-icon">
          <ExternalLink12 />
        </span>
      )}
    </a>
  );
}
